import { useRef } from "react";

import { Group } from "@mantine/core";
import { DateInput, TimeInput } from "@mantine/dates";
import { UseFormReturnType } from "@mantine/form";

import { IconCalendar, IconClock } from "@tabler/icons-react";

import ServiceDateTimezoneFields from "@/components/ServiceDateTimezoneFields/ServiceDateTimezoneFields";
import { EncounterServiceDateDetailsFieldsType } from "@/encounters/zod-schemas/encounter-service-date-details";
import { createMantineDateInputChangeHandler } from "@/utils/mantine-date-input";

interface EncounterServiceDateAmendmentFieldsProps<
  T extends {
    encounterDetails?: Partial<EncounterServiceDateDetailsFieldsType>;
  } = {
    encounterDetails?: Partial<EncounterServiceDateDetailsFieldsType>;
  },
> {
  form: UseFormReturnType<T>;
}

export default function EncounterServiceDateAmendmentFields<
  T extends {
    encounterDetails?: Partial<EncounterServiceDateDetailsFieldsType>;
  },
>({ form }: EncounterServiceDateAmendmentFieldsProps<T>) {
  const startTimeInputRef = useRef<HTMLInputElement>(null);
  const handleStartDateChange = createMantineDateInputChangeHandler(
    form,
    "encounterDetails.encounterStartDate",
  );
  const startTimeInputProps = form.getInputProps("encounterDetails.encounterStartTime");

  return (
    <div data-testid="encounter-service-date-amendment">
      <ServiceDateTimezoneFields
        showProviderMessage={false}
        timezoneInputTestId="amend-encounter-timezone-input"
        timezoneValue={form.values.encounterDetails?.encounterStartDateTimezone}
        timezoneInputProps={form.getInputProps("encounterDetails.encounterStartDateTimezone")}
      />
      <Group grow>
        <DateInput
          required
          clearable
          label="Date"
          allowDeselect={false}
          valueFormat="MM/DD/YYYY"
          leftSection={<IconCalendar size={18} color="var(--mantine-color-gray-6)" />}
          {...form.getInputProps("encounterDetails.encounterStartDate")}
          onChange={handleStartDateChange}
        />
        <TimeInput
          required
          label="Time"
          ref={startTimeInputRef}
          leftSection={<IconClock size={18} color="var(--mantine-color-gray-6)" />}
          {...startTimeInputProps}
          onClick={() => startTimeInputRef.current?.showPicker?.()}
        />
      </Group>
    </div>
  );
}
